import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { COMPANY, NAV } from "@/data/content";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "border-b border-slate-200 bg-white/85 backdrop-blur-md" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="container-x flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={COMPANY.logo} alt="SPMS Global" className="h-14 w-auto object-contain md:h-16" />
        </Link>

        <ul className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? scrolled ? "text-[color:var(--brand-blue)]" : "text-white"
                      : scrolled ? "text-slate-700 hover:text-slate-950" : "text-slate-200 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-getintouch"
            className="group hidden items-center gap-1.5 rounded-full bg-[color:var(--brand-red)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5 sm:inline-flex"
          >
            Get in Touch
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>

          <button
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-900 lg:hidden"
            data-testid="nav-hamburger"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <ul className="container-x flex flex-col py-4">
              {NAV.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                    className={({ isActive }) =>
                      `block border-b border-slate-100 py-4 text-lg font-medium ${
                        isActive ? "text-[color:var(--brand-blue)]" : "text-slate-800"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-getintouch"
                className="mt-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-[color:var(--brand-red)] px-5 py-3 text-base font-semibold text-white"
              >
                Get in Touch <ArrowUpRight className="h-4 w-4" />
              </Link>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
